/**
 * T29 — the turn where Zoe asks which question was meant.
 *
 * Rendered INSTEAD of `ZoeTurnCard`, not beside it. A clarification turn
 * carries no claims by construction, so the answer card would have nothing to
 * show; and presenting an empty answer next to a question would suggest Zoe
 * had tried and failed rather than that it is waiting on the user.
 *
 * Only the latest turn's options are clickable. An older clarification has
 * been superseded by whatever the thread did since — and the server enforces
 * the same rule, rejecting an answer to anything but the most recent
 * clarification — so leaving stale buttons live would offer an action that
 * silently does something other than what it appears to.
 *
 * When the question allows an answer of the user's own, the last row is a box
 * to write it in (T47): the options are the planner's guesses at what was
 * meant, and "by profit" must be one click away, not a hint to retype the
 * question.
 */

import { useState } from 'react';
import { Check, HelpCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PendingClarification } from '../../types/investigator';

interface Props {
  clarification: PendingClarification;
  /** False for settled history: the options render as a record, not as choices. */
  interactive: boolean;
  /** True while another turn is in flight — prevents a double submission. */
  isLoading?: boolean;
  /**
   * The option the user went on to pick, once the thread has moved past this
   * card. Marked so the history reads as a decision that was made, not as a
   * question that was left hanging.
   */
  chosenOptionId?: string;
  onSelect: (clarificationId: string, option: { id: string; label: string }) => void;
  /**
   * An answer in the user's own words, when the question allows one. Sent as
   * the next question: the server reads it as the reply to this card because
   * it is the thread's latest clarification. Omitted → the card only says
   * that the user may ask again in their own words.
   */
  onFreeText?: (text: string) => void;
}

export default function ZoeClarificationCard({
  clarification,
  interactive,
  isLoading = false,
  chosenOptionId,
  onSelect,
  onFreeText,
}: Props) {
  const options = clarification.options ?? [];
  const [ownAnswer, setOwnAnswer] = useState('');
  const freeTextOpen = interactive && clarification.free_text_allowed;

  const sendOwnAnswer = () => {
    const text = ownAnswer.trim();
    if (!text || isLoading || !onFreeText) return;
    onFreeText(text);
    setOwnAnswer('');
  };

  return (
    <div className="bg-card/40 rounded-xl border border-warning/40 p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <HelpCircle className="w-4 h-4 text-warning mt-0.5 shrink-0" aria-hidden />
        <p className="text-sm text-foreground leading-relaxed">{clarification.question}</p>
      </div>

      {options.length > 0 && (
        <ul className="space-y-2 list-none pl-0">
          {options.map((opt, i) => {
            const chosen = opt.id === chosenOptionId;
            return (
            <li key={opt.id}>
              <Button
                type="button"
                onClick={() => onSelect(clarification.clarification_id, opt)}
                disabled={!interactive || isLoading}
                aria-label={`Option ${i + 1}: ${opt.label}`}
                aria-pressed={chosen || undefined}
                variant="outline"
                className={
                  'w-full h-auto text-left px-3 py-2.5 ' +
                  (chosen
                    ? 'border-success/60 bg-success/20 cursor-default'
                    : interactive && !isLoading
                      ? 'border-accent bg-background/40 hover:border-warning hover:bg-background/70 cursor-pointer'
                      : 'border-border bg-background/20 cursor-default opacity-60')
                }
              >
                <span className="flex gap-2.5">
                  {chosen ? (
                    <Check className="w-4 h-4 text-success shrink-0 mt-0.5" aria-label="Chosen" />
                  ) : (
                    <span className="text-warning/80 text-sm font-medium shrink-0">{i + 1}.</span>
                  )}
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground">{opt.label}</span>
                    {opt.detail && (
                      <span className="block text-xs text-muted-foreground mt-0.5">{opt.detail}</span>
                    )}
                  </span>
                </span>
              </Button>
            </li>
            );
          })}
        </ul>
      )}

      {freeTextOpen && onFreeText && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendOwnAnswer();
          }}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-accent bg-background/40 focus-within:border-warning"
        >
          {options.length > 0 && (
            <span className="text-warning/80 text-sm font-medium shrink-0">{options.length + 1}.</span>
          )}
          <input
            type="text"
            value={ownAnswer}
            onChange={(e) => setOwnAnswer(e.target.value)}
            disabled={isLoading}
            placeholder="Something else? Type it here"
            aria-label="Your own answer"
            className="flex-1 min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-60"
          />
          <Button
            type="submit"
            variant="ghost"
            size="iconSm"
            disabled={isLoading || !ownAnswer.trim()}
            aria-label="Send your own answer"
            className="shrink-0 text-warning hover:text-warning hover:bg-transparent disabled:text-muted-foreground"
          >
            <Send className="w-4 h-4" aria-hidden />
          </Button>
        </form>
      )}

      {freeTextOpen && !onFreeText && (
        <p className="text-xs text-muted-foreground">
          Neither of these? Ask again in your own words.
        </p>
      )}
    </div>
  );
}
